import React, { useMemo, useState, useEffect } from 'react';
import { LayoutGrid, List, Search, PlusCircle, Download, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { PAGE_SIZE } from '../../constants';
import AssetCard from './AssetCard';
import MiniSparkline from '../common/MiniSparkline';

import { bulkUploadAssets, bulkFetchAssetsFromUrl } from '../../services/api';

const AssetManagerContent = ({
  assets, searchConfig, onSearchChange,
  sortConfig, onSortChange, pageConfig, onPageChange,
  onOpenAddModal, onOpenEditModal, onOpenDeleteConfirm,
  onAssetClick, onReloadAssets, isLoading, error
}) => {
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'list';
    return localStorage.getItem('assetViewMode') || 'list';
  });

  useEffect(() => {
    try { localStorage.setItem('assetViewMode', viewMode); } catch(e) {}
  }, [viewMode]);

  // Derived data for mini chart (circularity scores 0-100)
  const circularityData = useMemo(() => {
    if (!Array.isArray(assets)) return [];
    return assets
      .map(a => (a && a.circularity_score != null ? Number(a.circularity_score) : NaN))
      .filter(v => Number.isFinite(v) && v >= 0 && v <= 100);
  }, [assets]);



  // Bulk import state
  const [importFile, setImportFile] = useState(null);
  const [importUrl, setImportUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const { query } = searchConfig;
  const { sortBy, sortDirection } = sortConfig;
  const { currentPage } = pageConfig;


  const filteredAssets = useMemo(() => {
    if (!query) return assets;
    const lowerQuery = query.toLowerCase();
    return assets.filter(asset =>
      (asset.name && asset.name.toLowerCase().includes(lowerQuery)) ||
      (asset.id && asset.id.toLowerCase().includes(lowerQuery)) ||
      (asset.current_strategy && asset.current_strategy.toLowerCase().includes(lowerQuery)) ||
      (asset.material && asset.material.toLowerCase().includes(lowerQuery))
    );
  }, [assets, query]);

  const sortedAssets = useMemo(() => {
    if (!sortBy) return filteredAssets;
    return [...filteredAssets].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAssets, sortBy, sortDirection]);

  // Counts for Total Assets chart (reflect current search filter)
  const totalCount = assets.length;
  const filteredCount = filteredAssets.length;
  const assetsCountForChart = query ? filteredCount : totalCount;

  const totalPages = Math.ceil(sortedAssets.length / PAGE_SIZE);
  const paginatedAssets = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return sortedAssets.slice(start, end);
  }, [sortedAssets, currentPage]);

  const handlePrev = () => onPageChange(Math.max(1, currentPage - 1));
  const handleNext = () => onPageChange(Math.min(totalPages, currentPage + 1));

  const getSortIcon = (key) => {
    if (sortBy !== key) return null;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4 ml-1" /> : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  return (
    <div className="container mx-auto px-4 py-8">

      {(error && assets.length === 0) && (
        <div className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 flex items-start">
          <AlertTriangle className="w-5 h-5 mr-2 mt-0.5" />
          <div>
            <p className="font-medium">Failed to Load Assets</p>
            <p className="text-sm opacity-90">{String(error)}</p>
          </div>
        </div>
      )}

      {isLoading && assets.length === 0 && !error && (
        <div className="mb-4 p-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-700">Loading assets…</div>
      )}

      <h2 className="text-3xl font-extrabold text-gray-800 mb-6 flex items-center justify-between">
        <span className="flex items-center gap-3">
          <span className="flex items-center"><LayoutGrid className="w-7 h-7 mr-3 text-cyan-600" /> Asset Manager</span>
        </span>
      </h2>

      {/* Controls */}
      <div className="bg-white p-4 rounded-xl shadow-md mb-6 flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0 md:space-x-4">
          
        <div className="flex items-center space-x-2">
          <button
            onClick={onOpenAddModal}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition duration-150 flex items-center shadow-md"
          >
            <PlusCircle className="w-5 h-5 mr-2" /> Add Asset
          </button>
        </div>

        <div className="flex flex-row w-full md:w-auto relative">
          
            <input type="file" className="flex-1 border rounded p-2 text-sm p-1 border-gray-200 w-full mr-2 bg-white" accept=".csv, .xlsx, .xlsm, text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            <button disabled={!importFile || isImporting} onClick={async () => {
              setIsImporting(true); setImportMsg(null);
              try {
                const res = await bulkUploadAssets(importFile);
                setImportMsg(`Created ${res.created}, updated ${res.updated}, errors ${res.errors?.length || 0}`);
                if (onReloadAssets) onReloadAssets();
              } catch (e) {
                setImportMsg('Import failed');
              } finally { setIsImporting(false); }
            }} className={`ml-2 px-3 py-2 rounded text-white ${isImporting ? 'bg-gray-300' : 'bg-cyan-600 hover:bg-cyan-700'}`}>{isImporting ? 'Importing…' : 'Import'}</button>
      
        </div>
         
        <div className="flex-1 w-full md:w-auto relative">
            <div className="flex gap-2">
              <input 
                value={importUrl} 
                onChange={(e)=>setImportUrl(e.target.value)} 
                placeholder="Import from URL (CSV/XLSX/JSON)" 
                className="flex-1 border rounded p-2 text-sm p-1 border-gray-200 w-full mr-2 bg-white" />
              <button disabled={!importUrl || isImporting} onClick={async ()=>{
                setIsImporting(true); setImportMsg(null);
                try {
                  const res = await bulkFetchAssetsFromUrl(importUrl);
                  setImportMsg(`Created ${res.created}, updated ${res.updated}, errors ${res.errors?.length || 0}`);
                  if (onReloadAssets) onReloadAssets();
                } catch (e) { setImportMsg('Fetch failed'); } finally { setIsImporting(false); }
                }} className={`px-3 py-2 rounded text-white ${isImporting ? 'bg-gray-300' : 'bg-cyan-600 hover:bg-cyan-700'}`}>
                Fetch
             </button>
          </div>

            {importMsg && <p className="text-xs text-gray-600 mt-1">{importMsg}</p>}

        </div>

        <div className="flex-1 w-full md:w-auto relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ID, Name, or Type..."
            value={query}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>

       {/* Download CSV/PDF */}

        {/* <div className="flex items-center space-x-2">
          <button className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition duration-150 flex items-center shadow-md">
            <Download className="w-5 h-5 mr-2" /> Export
          </button>
        </div> */}
      </div>

  <div className="py-4 flex flex-col md:flex-row my-5  justify-between">

       <div className="mb-4 flex flex-col mr-auto">
         <MiniSparkline data={circularityData} width={200} height={80} label="Avg Circularity" />
        </div>
        <div className="mb-4 flex flex-col mr-0">
        <MiniSparkline
          data={[assetsCountForChart]}
          width={200}
          height={80}
          label="Total Assets"
          statValue={assetsCountForChart}
          statValueFormatter={(v) => `${v} assets`}
          valueFormatter={(v) => `${Math.round(v)} assets`}
          yDomain={[0, Math.max(5, assetsCountForChart)]}
          hideCountFooter={true}
        />
        </div>

  </div>


      {/* Sorting Bar */}
      <h2 className="text-3xl font-extrabold text-gray-800 mb-6 flex items-center justify-between">
      
      <div className="bg-gray-100 p-3 rounded-t-xl text-sm font-medium text-gray-700 flex justify-between items-center">
        <div className="flex flex-col md:flex-row items-center space-x-2">
         
          <span className="bg-yellow-600 px-2 py-1 text-sm text-white rounded">Sort by:</span>

          <button onClick={() => onSortChange('initial_value')} 
            className={`px-3 py-1 text-sm rounded-full ${sortBy === 'initial_value' ? 'bg-cyan-100 text-cyan-700' : 'text-gray-600 hover:bg-gray-100'}`}>
            Initial Value {sortBy === 'initial_value' && (sortDirection === 'asc' ? '↑' : '↓')}
          </button>
          <button onClick={() => onSortChange('current_value')} className={`px-3 py-1 text-sm rounded-full ${sortBy === 'current_value' ? 'bg-cyan-100 text-cyan-700' : 'text-gray-600 hover:bg-gray-100'}`}>
            Current Value {sortBy === 'current_value' && (sortDirection === 'asc' ? '↑' : '↓')}
          </button>
          <button onClick={() => onSortChange('age_months')} className={`px-3 py-1 text-sm rounded-full ${sortBy === 'age_months' ? 'bg-cyan-100 text-cyan-700' : 'text-gray-600 hover:bg-gray-100'}`}>
            Age {sortBy === 'age_months' && (sortDirection === 'asc' ? '↑' : '↓')}
          </button>
        </div>
      </div>

      {/* Asset List/Grid toggler*/}
        <span className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            className={`px-3 py-1 text-sm font-medium flex items-center ${viewMode === 'grid' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            aria-pressed={viewMode === 'grid'}
          >
            <LayoutGrid className="w-4 h-4 mr-1" /> Grid
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`px-3 py-1 text-sm font-medium flex items-center border-l border-gray-300 ${viewMode === 'list' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            aria-pressed={viewMode === 'list'}
          >
            <List className="w-4 h-4 mr-1" /> List
          </button>
        </span>
      </h2>


      {paginatedAssets.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-4">
            {paginatedAssets.map(asset => (
              <AssetCard
                key={asset.id}
                asset={asset}
                onEdit={onOpenEditModal}
                onDelete={onOpenDeleteConfirm}
                onClick={onAssetClick}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Strategy (Current vs AI)', 'Condition', 'Current Value', 'Circularity', 'Action'].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedAssets.map(asset => {
                  const conditionRating = Number(asset.condition_rating || 0);
                  const conditionText = conditionRating >= 4 ? 'Excellent' : conditionRating >= 3 ? 'Good' : conditionRating >= 2 ? 'Fair' : conditionRating >= 1 ? 'Poor' : 'Critical';
                  const currentValue = Number(asset.current_market_value || 0);
                  const circularity = Number(asset.circularity_score || 0);
                  const strategyKey = asset.current_strategy || 'R7';
                  return (
                    <tr key={asset.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900 cursor-pointer" onClick={() => onAssetClick && onAssetClick(asset.id)}>
                        <div className="font-semibold">{asset.name}</div>
                        <div className="text-xs text-gray-500">ID: {asset.id}</div>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700 border border-gray-200" title="Current user-selected strategy">{strategyKey || '—'}</span>
                          <span className="text-xs text-gray-400">vs</span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs border ${asset.ai_recommended_r_strategy ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}
                            title={asset.ai_recommended_r_strategy ? 'AI-recommended R strategy' : 'No AI recommendation yet'}
                          >
                            {asset.ai_recommended_r_strategy || 'AI —'}
                          </span>
                        </div>
                        {(asset.current_end_of_life_strategy || asset.ai_recommended_end_of_life_strategy) && (
                          <div className="mt-1 text-[11px] text-gray-600 truncate max-w-[280px]">
                            <span className="font-medium text-gray-500">EOL:</span>
                            <span className="ml-1" title={asset.current_end_of_life_strategy || '—'}>
                              {asset.current_end_of_life_strategy || '—'}
                            </span>
                            <span className="mx-1 text-gray-400">vs</span>
                            <span className={`ml-0.5 ${asset.ai_recommended_end_of_life_strategy ? 'text-green-700' : 'text-gray-400'}`} title={asset.ai_recommended_end_of_life_strategy || 'No AI recommendation'}>
                              {asset.ai_recommended_end_of_life_strategy || 'AI —'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">{conditionText} ({conditionRating.toFixed(1)}/5.0)</td>
                      <td className="px-4 py-2 text-sm font-medium">${currentValue.toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm">
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2 mr-2"><div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.max(0, Math.min(100, circularity))}%` }} /></div>
                          <span className="text-gray-700 font-medium">{circularity.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <div className="flex items-center gap-3">
                          <button onClick={() => onAssetClick && onAssetClick(asset.id)} className="text-cyan-600 hover:text-cyan-800">View</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="text-center py-12 bg-white rounded-xl shadow-md mt-4">
          <AlertTriangle className="w-10 h-10 mx-auto text-yellow-500" />
          <p className="mt-2 text-lg font-medium text-gray-700">No assets found.</p>
          <p className="text-sm text-gray-500">Try adjusting your search or filters.</p>
        </div>
      )}

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-6 p-4 bg-white rounded-xl shadow-md">
        <span className="text-sm text-gray-600">
          Showing {Math.min(PAGE_SIZE, paginatedAssets.length)} of {filteredAssets.length} total results
        </span>
        <div className="flex space-x-2">
          <button onClick={handlePrev} disabled={currentPage === 1} className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100 transition">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="px-4 py-2 border border-cyan-500 bg-cyan-50 text-cyan-800 rounded-lg text-sm font-medium">
            Page {currentPage} of {totalPages}
          </span>
          <button onClick={handleNext} disabled={currentPage === totalPages || totalPages === 0} className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100 transition">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
  </div>
  );
};

export default AssetManagerContent;
